/**
 * AccessCodeBadge — single-line compact code display for the driver stop card.
 *
 * Phase 2 (Gate Codes). Per the May 15 design feedback: card real estate is
 * precious; show only what the driver needs at a glance. The full details
 * (instructions, provenance, reporting) live inside Update Status (the
 * DeliveryConfirmationSheet) — see AccessCodeDetail.jsx.
 *
 * Props:
 *   code:       AccessCodeLookupResult — the access code record.
 *   index:      number — 0-based position within this stop's codes.
 *   total:      number — total codes on this stop (controls numbering).
 */
import React from 'react';
import { KeyRound, NotebookPen } from 'lucide-react';

export default function AccessCodeBadge({ code, index, total }) {
  const label = total > 1 ? `Code ${index + 1}` : 'Code';
  const hasInstructions = Boolean(code.instructions && code.instructions.trim());

  return (
    <div
      className="mt-1.5 flex items-center gap-1.5 text-sm text-stone-700"
      data-testid={`access-code-badge-${code.id}`}
    >
      <KeyRound className="h-4 w-4 text-amber-700 flex-shrink-0" />
      <span className="text-stone-600">{label}:</span>
      <span className="font-mono font-bold text-stone-900 tracking-wider">
        {code.code}
      </span>
      {hasInstructions && (
        <NotebookPen
          className="h-3.5 w-3.5 text-amber-700 flex-shrink-0"
          aria-label="Has additional instructions — see Update Status"
          data-testid={`access-code-has-notes-${code.id}`}
        />
      )}
    </div>
  );
}
